import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Modal, Alert, Space, Button, Spin, message } from 'antd';
import intl from 'react-intl-universal';
import { formatDate } from '@/utils';
import { getTimeByUtcOffset } from '@/utils/intl';
import { DATE_TIME_FORMAT } from '@/config';
import Card from '@/utils/card';
import { isNil } from '@/utils/number';
import * as CardApi from '@/api/card';
import useUploadDoorLock, { CardUsage, UploadStatus } from '@/hooks/useUploadDoorLock';

import { CARD_ERROR_MAP, LockVendor } from './config';
import { CreateCardProps } from './interface';
import { isBlankCardFn } from './util';
import { transformParamsC2PMS } from './helper';
import styles from './index.module.scss';

const CreateCardModal: React.FC<CreateCardProps> = ({
  createModalVisibile,
  roomId,
  roomNo,
  // pms中心已启用的房卡
  enabledVendor,
  ...restProps
}) => {
  const defaultRoomInfo = {
    dai: 0,
    recNo: '',
    resvNo: '',
    lockNo: '',
    checkInTime: '',
    checkOutTime: '',
    roomId: 0,
    roomNo: '',
  };

  const [hotelId, setHotelId] = useState(null);
  const [card, setCard] = useState(null);
  const [rafId, setRafId] = useState(0);
  const [roomInfoLoading, setRoomInfoLoading] = useState(false);
  const [roomInfo, setRoomInfo] = useState(defaultRoomInfo);
  const [errorCard, setErrorCard] = useState(false);

  const { startUploadDoorInfo, endUploadDoorInfo } = useUploadDoorLock();

  // 获取酒店id(H21需要)
  const fetchHotelId = () => {
    return CardApi.fetchHotelId();
  };

  // 初始化房间信息
  const initRoomInfo = async () => {
    setRoomInfoLoading(true);
    try {
      const res = await CardApi.fetchRoomInfo({
        roomId,
      });

      setRoomInfoLoading(false);

      if (res && res.status) {
        setRoomInfo({
          dai: res.data?.dai,
          recNo: res.data?.recNo,
          resvNo: res.data?.resvNo,
          lockNo: res.data?.lockNo,
          checkInTime: res.data?.validityStartTime,
          checkOutTime: res.data?.validityEndTime,
          roomId: res.data?.roomId,
          roomNo: res.data?.roomNo,
        });
      }
    } catch (e) {
      setRoomInfoLoading(false);
      setRoomInfo(defaultRoomInfo);
    }
  };

  // 保存房卡信息
  const saveCardInfo = async (data) => {
    try {
      const res = await CardApi.crateRoomCard({
        cardNo: transformParamsC2PMS(enabledVendor.lockCode, data)?.serialnumber,
        roomId,
        hotelId: enabledVendor.lockCode === LockVendor.H21 ? hotelId : data?.hotelid,
        dai: roomInfo.dai,
        recNo: roomInfo.recNo,
        resvNo: roomInfo.resvNo,
        lockNo: roomInfo.lockNo,
        roomNo: roomInfo.roomNo,
        validityStartTime: roomInfo.checkInTime,
        validityEndTime: roomInfo.checkOutTime,
      });

      if (res && res.status) {
        message.success(intl.get('cpm_cards_make_success'));
        if (restProps.createCallBack && typeof restProps.createCallBack === 'function')
          restProps.createCallBack();
      } else {
        message.error(res.msg || intl.get('cpm_cards_info_save_fail'));
      }
    } catch (e) {
      // message.error('房卡信息保存失败');
      setErrorCard(true);
      throw e;
    }
  };

  // 写卡
  const writeCard = () => {
    let params = {};
    if (enabledVendor.lockCode === LockVendor.H21) {
      params = {
        hotelid: hotelId,
        dai: roomInfo.dai,
        lockno: roomInfo.lockNo,
        effecteddate: formatDate(roomInfo.checkInTime, 'YYMMDDHHmm'),
        expireddate: formatDate(roomInfo.checkOutTime, 'YYMMDDHHmm'),
      };
    }
    if (enabledVendor.lockCode === LockVendor.BD) {
      params = {
        lockno: roomInfo.lockNo,
        effecteddate: formatDate(roomInfo.checkInTime, 'YYMMDDHHmm'),
        expireddate: formatDate(roomInfo.checkOutTime, 'YYMMDDHHmm'),
        username: enabledVendor.dbRoot,
        password: enabledVendor.dbPass,
        isguest: 1,
      };
    }
    // pengze effecteddate 这个时间最多只能比当前时间早一个小时，否则会导致开锁失败
    if ([LockVendor.YG, LockVendor.CHUANGJIA, LockVendor.PengZe].includes(enabledVendor.lockCode)) {
      params = {
        lockno: roomInfo.lockNo,
        effecteddate: formatDate(roomInfo.checkInTime, 'YYMMDDHHmm'),
        expireddate: formatDate(roomInfo.checkOutTime, 'YYMMDDHHmm'),
      };
    }

    card
      .writeRoomCard(params)
      .then(async (data) => {
        await saveCardInfo(data);

        endUploadDoorInfo({ result: UploadStatus.Success });
      })
      .catch((e) => {
        // message.error(e.error || '写卡失败');
        setErrorCard(true);

        endUploadDoorInfo({ result: UploadStatus.Fail, remark: e.error || e.msg });
      });
  };

  // 根据品牌区分读卡参数
  const generateReadParams = () => {
    let result = null;

    if (enabledVendor.lockCode === LockVendor.H21) {
      result = {
        hotelid: hotelId,
      };
    }

    if (enabledVendor.lockCode === LockVendor.BD) {
      result = {
        username: enabledVendor.dbRoot,
        password: enabledVendor.dbPass,
      };
    }

    return result;
  };

  let start;
  // 循环读卡
  const readCard = (timestamp) => {
    if (start === undefined) {
      start = timestamp;
    }

    const elapsed = timestamp - start;
    if (elapsed > 500 && card !== null && card.connected) {
      card
        .readRoomCard(generateReadParams())
        .then((data) => {
          const isBlank = isBlankCardFn(enabledVendor, data);
          if (isBlank) {
            writeCard();
          } else {
            setErrorCard(true);

            endUploadDoorInfo({
              result: UploadStatus.Fail,
              remark: intl.get('device_door_no_empty_card'),
            });
          }

          // clear loop
          if (rafId) cancelAnimationFrame(rafId);
        })
        .catch((e) => {
          // 未放卡或卡片未放好
          if (e.code === CARD_ERROR_MAP.noCard) {
            setErrorCard(false);
            start = undefined;
            setRafId(requestAnimationFrame(readCard));
          } else {
            setErrorCard(true);

            endUploadDoorInfo({ result: UploadStatus.Fail, remark: e.error });
            // message.error(e.error || '读卡失败');
            // clear loop
            if (rafId) cancelAnimationFrame(rafId);
          }
        });
    } else {
      setRafId(requestAnimationFrame(readCard));
    }
  };

  // 重试
  const retry = () => {
    startUploadDoorInfo({ type: CardUsage.Create, roomNo });

    setErrorCard(false);
    start = undefined;
    setRafId(requestAnimationFrame(readCard));
  };

  const resetStates = () => {
    if (card !== null && card.connected) card.close();
    setCard(null);
    setErrorCard(false);
    setRoomInfo(defaultRoomInfo);
    // clear loop
    if (rafId) cancelAnimationFrame(rafId);
  };

  const closeModal = () => {
    if (restProps.createCallBack && typeof restProps.createCallBack === 'function')
      restProps.createCallBack();
  };

  useEffect(() => {
    if (createModalVisibile) {
      startUploadDoorInfo({ type: CardUsage.Create, roomNo });

      // H21需要先获取酒店id
      if (enabledVendor.lockCode === LockVendor.H21) {
        fetchHotelId().then((res) => {
          setHotelId(Number(res.data));
        });
      }

      // init ws connection
      const c = new Card();
      setCard(c);

      initRoomInfo();
    } else {
      resetStates();
    }
  }, [createModalVisibile]);

  useEffect(() => {
    if (enabledVendor.lockCode && card && !isNil(roomInfo.lockNo)) {
      if (enabledVendor.lockCode !== LockVendor.H21) {
        // loop read card request
        setRafId(requestAnimationFrame(readCard));
      }

      if (enabledVendor.lockCode === LockVendor.H21 && !isNil(hotelId)) {
        // H21需要先读卡
        setRafId(requestAnimationFrame(readCard));
      }
    }
  }, [enabledVendor, roomInfo, hotelId, card]);

  useEffect(() => {
    return () => {
      resetStates();
    };
  }, []);

  const modalFooter = () => {
    if (errorCard) {
      return (
        <Space align="start" size={16}>
          <Button type="primary" onClick={() => retry()}>
            {intl.get('app_confirm_retry_btn')}
          </Button>
          <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>
        </Space>
      );
    }

    return <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>;
  };

  return (
    <Modal
      width={560}
      title={intl.get('cpm_cards_make')}
      visible={createModalVisibile}
      maskClosable={false}
      keyboard={false}
      footer={modalFooter()}
      destroyOnClose
      onCancel={() => closeModal()}
    >
      {errorCard ? (
        <Alert message={intl.get('cpm_cards_status_abnormal_canot_make')} type="error" showIcon />
      ) : (
        <Alert message={intl.get('cpm_cards_put_empty_on_write_mechine')} type="info" showIcon />
      )}
      <Spin spinning={roomInfoLoading}>
        <ul className={styles.cardInfo}>
          <li>
            <label>{intl.get('app_room')}</label>
            <span>{roomInfo.roomNo || '-'}</span>
          </li>
          <li>
            <label>{intl.get('device_lock_number')}</label>
            <span>{roomInfo.lockNo || '-'}</span>
          </li>
          <li>
            <label>{intl.get('manage_license_field_start_label')}</label>
            <span>{getTimeByUtcOffset(roomInfo.checkInTime, DATE_TIME_FORMAT) || '-'}</span>
          </li>
          <li>
            <label>{intl.get('cpm_cards_end_validity_period')}</label>
            <span>{getTimeByUtcOffset(roomInfo.checkOutTime, DATE_TIME_FORMAT) || '-'}</span>
          </li>
        </ul>
      </Spin>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  const { roomCardEnabled, enabledVendor } = state.card;
  return { roomCardEnabled, enabledVendor };
};

export default connect(mapStateToProps, null)(CreateCardModal);
